import type { HTMLAttributes } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import {
  useFetchHotelPromotions,
  useHotelPromotion,
} from '@/app/components/hotel-detail/hooks/useFetchHotelPromotions';
import { Text } from '@lmn/design-system';
import { useFetchRooms } from '@/app/components/hotel-detail/hooks/useFetchRooms';
import { useIsMobile } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useTranslation } from '@/app/components/translations/Translation';
import { LmnDealMessage } from '@/app/components/hotel-detail/LmnDealMessage';
import LastMinuteDealsBadge from '@/app/common/components/Promotions/LastMinuteDealsBadge';
import { LastMinuteDealsBadgeContainer } from '@/app/common/components/Promotions/LastMinuteDealsBadge/styled';
import { flashSalesGradient, lastMinuteDealsGradient } from '@/app/common/components/Promotions/styled';
import { starfishCssUrl } from '@/app/common/icons/starfish';
import { Icon } from '@/app/common/icons/Icon';
import { FLASH_SALES_ICON, flashSalesIconSize } from '@/app/common/icons/promotionIcons';

const EvoLmnDealsExtrasContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media ${customCss.breakpoints.sm} {
    gap: 4px;
    max-width: 900px;
  }
`;

const EvoLmnDealsExtrasRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const CheckIcon = styled.span<{ $isLmnDeal: boolean }>`
  display: block;
  flex-shrink: 0;
  width: 20px;
  height: 24px;
  background: ${({ $isLmnDeal }) => ($isLmnDeal ? lastMinuteDealsGradient : flashSalesGradient)};
  mask: url('${starfishCssUrl('Semantics', 'icon-check')}') no-repeat center / 20px 20px;
  -webkit-mask: url('${starfishCssUrl('Semantics', 'icon-check')}') no-repeat center / 20px 20px;
`;

const LmnDealExtrasOuter = styled.div<{ $isLmnDeal: boolean }>`
  position: relative;
  border-radius: 16px;
  background: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};

  padding: ${({ theme }) => theme.kallisto.space.space200};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space200};

  /* Gradient 1px border via mask-composite — renders evenly on all edges (the
     padding-box/border-box double-background hack darkens the right/bottom edges). */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: ${({ $isLmnDeal }) => ($isLmnDeal ? lastMinuteDealsGradient : flashSalesGradient)};
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  ${EvoLmnDealsExtrasRow} {
    ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  }

  @media ${customCss.breakpoints.sm} {
    padding: ${({ theme }) => theme.kallisto.space.space250} ${({ theme }) => theme.kallisto.space.space300};
  }
`;

const LmnDealExtrasList = styled.ul`
  list-style: none;
  padding-left: 0;

  li {
    position: relative;
    padding-left: 28px;

    &::before {
      content: '•';
      position: absolute;
      left: 8px;
      font-size: 1em;
      line-height: 1;
      top: 0.3em;
    }
  }
`;

const Separator = styled.div`
  height: 1px;
  background-color: ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
`;

const PerksToggle = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  color: inherit;
`;

function EvoLmnDealsExtras({ extras = [] }: { extras?: string[] }) {
  const { showLmnDealsPromotion } = useHotelPromotion();

  if (!extras || extras.length === 0) {
    return null;
  }

  return (
    <EvoLmnDealsExtrasContainer>
      {extras.map((extra, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <EvoLmnDealsExtrasRow key={`extra.${index}`}>
          {/* Decorative: every row is a check, so the icon adds no info beyond the section context. */}
          <CheckIcon $isLmnDeal={!!showLmnDealsPromotion} aria-hidden="true" />
          <Text variant="Body">{extra}</Text>
        </EvoLmnDealsExtrasRow>
      ))}
    </EvoLmnDealsExtrasContainer>
  );
}

function MobileLmnDealExtras({ extras }: { extras: string[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const count = extras.length;
  const title =
    count === 1
      ? t('vacation.select.cc.lmdeals.extras.title.mobile.single')
      : t('vacation.select.cc.lmdeals.extras.title.mobile.plural', { count: String(count) });

  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        gap: 8px;
      `}
    >
      <PerksToggle
        type="button"
        aria-expanded={isOpen}
        aria-controls="evo-lmn-deal-perks"
        onClick={() => setIsOpen((open) => !open)}
      >
        <Text variant="Title5">{title}</Text>
        <Icon
          category="Arrows"
          name="icon-chevron-right"
          size={12}
          footprint={20}
          color="iconSecondary"
          alt=""
          css={`
            transform: rotate(${isOpen ? '-90deg' : '90deg'});
          `}
        />
      </PerksToggle>
      {isOpen && (
        <div id="evo-lmn-deal-perks">
          <EvoLmnDealsExtras extras={extras} />
        </div>
      )}
    </div>
  );
}

export function EvoLmnDealExtras(props: HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  const { commonData } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { data: promotions } = useFetchHotelPromotions();
  const { data: roomsData } = useFetchRooms();
  // MPH-744: brands without the lastMinuteDeals branding get the flash-sales treatment — the
  // flash-sales border gradient and the same header (icon + title) the non-EVO
  // PackageDetailsModal shows; the check icons below already follow the same flag.
  const { showLmnDealsPromotion } = useHotelPromotion();

  const { promotionAddedValues } = promotions || {};
  const showLmnDealBadge = (roomsData as RoomsServiceResponseType)?.anyFlashSales;

  return (
    <LmnDealExtrasOuter $isLmnDeal={!!showLmnDealsPromotion} {...props}>
      <div
        css={`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        {showLmnDealBadge &&
          (showLmnDealsPromotion ? (
            <LastMinuteDealsBadge
              skipPeriodicPromotion={true}
              fontPreset={css`
                ${({ theme }) => theme.kallisto.mixins.font('Title4')};
                font-weight: 400;
              `}
              iconSize={20}
              css={css`
                align-items: flex-start;
                justify-content: flex-start;

                ${LastMinuteDealsBadgeContainer} {
                  img {
                    margin-right: 10px;
                    margin-top: 4px;
                  }
                }

                strong {
                  line-height: 24px;
                }

                strong::after {
                  bottom: 0;
                }
              `}
            />
          ) : (
            <div
              css={`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <Icon {...FLASH_SALES_ICON} size={flashSalesIconSize(20)} footprint={20} />
              <Text variant="Title4">{t('vacation.select.flashsales.banner.title')}</Text>
            </div>
          ))}
        {/* Without LMN branding and before the deal dates load, the list has no bullets — drop it
            so the empty <ul> doesn't hold a blank flex-gap slot under the header. */}
        {(showLmnDealsPromotion || !!promotions?.bookDate) && (
          <LmnDealExtrasList>
            {/* "Look out for rooms with the Last Minute deals label." only makes sense when the
                rooms actually carry that label, i.e. with the LMN-deals branding on. */}
            {showLmnDealsPromotion && (
              <li>
                <Text variant="Body">{t('vacation.select.cc.lmdeals.extras.badge_explanation')}</Text>
              </li>
            )}
            {promotions?.bookDate && (
              <li>
                <Text variant="Body">
                  <LmnDealMessage
                    bookDate={promotions.bookDate}
                    checkin={promotions.checkin}
                    checkout={promotions.checkout}
                  />
                </Text>
              </li>
            )}
          </LmnDealExtrasList>
        )}
      </div>
      {promotionAddedValues && promotionAddedValues.length > 0 ? (
        isMobile ? (
          <>
            <Separator />
            <MobileLmnDealExtras extras={promotionAddedValues} />
          </>
        ) : (
          <>
            <Separator />
            <div
              css={`
                display: flex;
                flex-direction: column;
                gap: 8px;
              `}
            >
              <Text variant="Title5">{t('vacation.select.cc.lmdeals.extras.title')}</Text>
              <EvoLmnDealsExtras extras={promotionAddedValues} />
            </div>
          </>
        )
      ) : null}
    </LmnDealExtrasOuter>
  );
}
